import { faFileCsv, faFileExcel } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Card, CardContent, Grid, IconButton, Tooltip as MuiTooltip, Skeleton, Stack, Typography, useMediaQuery } from '@mui/material';
import { Theme } from '@mui/system';
import { parse as parseCsv } from 'json2csv';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import * as XLSX from 'xlsx';
import { objectIdToDate } from '../../../../../../utils/common';

interface TransformedDataPoint {
    date: string;
    claimsCount: number;
    subscribeCounts: number;
}

interface ExportData {
    headers: { label: string; value: string }[];
    rows: TransformedDataPoint[];
}

const generateData = (
    data: {
        _id: string;
        payload: {
            claimsCount: number;
            clicksCounts?: number;
            subscribeCounts: number;
        };
    }[],
): { data: { time: number; subscribeCounts: number; claimsCount: number }[] } => {
    const result = {
        data: data.map(d => {
            return {
                time: objectIdToDate(d._id).valueOf(),
                subscribeCounts: d.payload.subscribeCounts,
                claimsCount: d.payload.claimsCount,
            };
        }),
    };

    return result;
};

/**
 * Transforms the trial counters data rows from the server to a format ready for export to CSV or Excel
 */
const transformCountersForExport = (
    data:
        | {
              _id: string;
              payload: {
                  claimsCount: number;
                  clicksCounts?: number;
                  subscribeCounts: number;
              };
          }[]
        | undefined,
    timezone: string,
): ExportData => {
    if (!data || data.length === 0) {
        return { headers: [], rows: [] };
    }

    const headers = [
        { label: `Date (${timezone})`, value: 'date' },
        { label: 'Claims', value: 'claimsCount' },
        { label: 'Subscriptions', value: 'subscribeCounts' },
    ];

    const rows: TransformedDataPoint[] = data.map(d => ({
        date: moment(objectIdToDate(d._id)).tz(timezone).format('L'),
        claimsCount: d.payload.claimsCount,
        subscribeCounts: d.payload.subscribeCounts,
    }));

    return { headers, rows };
};

/**
 * Utility function to handle data export
 */
const exportData = (
    data:
        | {
              _id: string;
              payload: {
                  claimsCount: number;
                  clicksCounts?: number;
                  subscribeCounts: number;
              };
          }[]
        | undefined,
    metricId: string,
    timezone: string,
    format: 'csv' | 'excel',
): void => {
    const transformedData: ExportData = transformCountersForExport(data, timezone);
    const filename = `claimsAndSubscriptionsByDate_${metricId}_${moment().tz(timezone, true).format('YYYY-MM-DD')}`;

    if (format === 'csv') {
        const csv = parseCsv(transformedData.rows, {
            fields: transformedData.headers,
            transforms: [],
        });
        const blob: Blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(new Blob([blob]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${filename}.csv`);
        document.body.appendChild(link);
        link.click();
        link.parentNode && link.parentNode.removeChild(link);
    } else if (format === 'excel') {
        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet([]);
        XLSX.utils.sheet_add_aoa(worksheet, [transformedData.headers.map(header => header.label)]);
        XLSX.utils.sheet_add_json(worksheet, transformedData.rows, {
            origin: 'A2',
            skipHeader: true,
            header: transformedData.headers.map(header => header.value),
        });
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Counters');
        XLSX.writeFile(workbook, `${filename}.xlsx`);
    }
};

const CustomTooltip = ({ active, payload, theme }: any) => {
    if (active && payload && payload.length) {
        return (
            <div>
                <Grid
                    container
                    flexGrow={0}
                    alignItems="center"
                    spacing={1}
                    sx={{
                        backgroundColor: '#fff',
                        borderRadius: 4,
                        border: '1px solid #999',
                        maxWidth: 200,
                    }}
                >
                    <Grid item xs={12}>
                        <span style={{ fontWeight: 'bold' }}>{moment(payload[0].payload.time).format('ddd Do, hh:mm A')}</span>
                    </Grid>
                    {payload.map((item: any, index: number) => (
                        <Grid item xs={12} key={index}>
                            {item.dataKey === 'claimsCount' && (
                                <Grid container flexGrow={1} alignItems="center">
                                    <Grid item xs={4} sx={{ color: theme.palette.primary.main }}>
                                        Claims:
                                    </Grid>
                                    <Grid item xs={8} textAlign={'right'} sx={{ color: theme.palette.primary.main, paddingRight: '8px' }}>
                                        {item.value && typeof item.value === 'number' ? item.value.toLocaleString() : 0}
                                    </Grid>
                                </Grid>
                            )}

                            {item.dataKey === 'subscribeCounts' && (
                                <Grid container flexGrow={1} alignItems="center">
                                    <Grid item xs={4} sx={{ color: theme.palette.secondary.main }}>
                                        Subscriptions:
                                    </Grid>
                                    <Grid item xs={8} textAlign={'right'} sx={{ color: theme.palette.primary.main, paddingRight: '8px' }}>
                                        {item.value && typeof item.value === 'number' ? item.value.toLocaleString() : 0}
                                    </Grid>
                                </Grid>
                            )}
                        </Grid>
                    ))}
                </Grid>
            </div>
        );
    }

    return null;
};

type Props = {
    metricId: string;
    trialCounters:
        | {
              metricsDatas:
                  | {
                        _id: string;
                        payload: {
                            claimsCount: number;
                            clicksCounts?: number;
                            subscribeCounts: number;
                        };
                    }[]
                  | undefined;
          }
        | undefined;
    trialCountersLoading: boolean;
    timezone: string;
    theme: Theme;
};

const SextforceMetricsTrialCountersGraph = (props: Props) => {
    const { metricId, trialCounters, trialCountersLoading, timezone, theme } = props;
    const [dataMain, setDataMain] = useState<{ data: { time: number; subscribeCounts: number; claimsCount: number }[] }>({
        data: [],
    });
    const largeScreen = useMediaQuery(theme.breakpoints.up('sm'));

    useEffect(() => {
        if (!trialCountersLoading && trialCounters && trialCounters.metricsDatas && trialCounters.metricsDatas.length > 0) {
            setDataMain(generateData(trialCounters.metricsDatas));
        } else {
            setDataMain({ data: [] });
        }
    }, [trialCounters, trialCountersLoading]);

    const handleDownloadReportCsv = () => {
        exportData(trialCounters?.metricsDatas, metricId, timezone, 'csv');
    };

    const handleDownloadReportExcel = () => {
        exportData(trialCounters?.metricsDatas, metricId, timezone, 'excel');
    };

    const Chart = (chartData: {
        data: {
            time: number;
            subscribeCounts: number;
            claimsCount: number;
        }[];
    }) => (
        <ResponsiveContainer width="100%" height="100%">
            <LineChart
                data={chartData && chartData.data ? chartData.data : []}
                margin={{
                    top: 0,
                    right: largeScreen ? -10 : -20,
                    left: largeScreen ? -10 : -20,
                    bottom: 50,
                }}
            >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                    dataKey="time"
                    type="number"
                    scale={'time'}
                    name="Time"
                    tickFormatter={value => (value && value !== 'auto' ? moment(value).tz(timezone).format('L') : '')}
                    domain={chartData && chartData.data && chartData.data.length > 0 ? ['dataMin', 'dataMax'] : ['auto', 'auto']}
                    interval={'preserveStartEnd'}
                    tick={{ fontSize: 13 }}
                    angle={-90}
                    dy={35}
                    dx={-5}
                />
                <YAxis
                    yAxisId="claimsCount"
                    dataKey={'claimsCount'}
                    scale={'linear'}
                    name="Claims Count"
                    domain={[(dataMin: number) => Math.abs(dataMin * 0.9), (dataMax: number) => Math.abs(dataMax * 1.1)]}
                    tickFormatter={(value: number) => value.toLocaleString()}
                    tick={{ fill: theme.palette.primary.main, fontSize: 13 }}
                />
                <Line
                    yAxisId="claimsCount"
                    type="linear"
                    dataKey="claimsCount"
                    stroke={theme.palette.primary.main}
                    activeDot={{ r: 0 }}
                    dot={{ r: 0 }}
                />
                <YAxis
                    yAxisId="subscribeCounts"
                    dataKey={'subscribeCounts'}
                    scale={'linear'}
                    name="Subscribers Count"
                    domain={[0, (dataMax: number) => Math.abs(dataMax * 1.1)]}
                    tickFormatter={(value: number) => value.toLocaleString()}
                    orientation="right"
                    tick={{ fill: theme.palette.secondary.main, fontSize: 13 }}
                />
                <Line
                    yAxisId="subscribeCounts"
                    type="linear"
                    dataKey="subscribeCounts"
                    stroke={theme.palette.secondary.main}
                    activeDot={{ r: 0 }}
                    dot={{ r: 0 }}
                />
                <Tooltip content={<CustomTooltip payload={chartData.data} theme={theme} />} />
                <Legend
                    verticalAlign="top"
                    height={36}
                    formatter={(value: string) => (
                        <span style={{ fontSize: 13 }}>{value === 'subscribeCounts' ? 'Subscribers Count' : 'Claims Count'}</span>
                    )}
                />
            </LineChart>
        </ResponsiveContainer>
    );

    return (
        <Card sx={{ width: '100%', marginBottom: 2 }}>
            <CardContent>
                <Stack direction="column" spacing={1} sx={{ marginBottom: 1 }}>
                    <Stack direction="row" spacing={1} alignItems={'center'} justifyContent={'space-between'}>
                        <Typography variant="h5" sx={{ marginBottom: theme.spacing(1) }}>
                            Claims Count and Subscriptions Count by Date
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems={'center'}>
                            <MuiTooltip title="Download as CSV">
                                <span>
                                    <IconButton
                                        disabled={trialCountersLoading}
                                        onClick={handleDownloadReportCsv}
                                        aria-label="Download as CSV"
                                    >
                                        <FontAwesomeIcon icon={faFileCsv} size="2x" color={theme.palette.info.main} />
                                    </IconButton>
                                </span>
                            </MuiTooltip>
                            <MuiTooltip title="Download as Excel">
                                <span>
                                    <IconButton
                                        disabled={trialCountersLoading}
                                        onClick={handleDownloadReportExcel}
                                        aria-label="Download as Excel"
                                    >
                                        <FontAwesomeIcon icon={faFileExcel} size="2x" color={theme.palette.info.main} />
                                    </IconButton>
                                </span>
                            </MuiTooltip>
                        </Stack>
                    </Stack>
                    <Typography variant="body1" sx={{ marginBottom: theme.spacing(1) }}>
                        The following graph shows how many fans claimed the trial or promo, and how many subscribed via this link, sorted by
                        date since the start of the campaign.
                    </Typography>
                </Stack>
                <Grid container flexGrow={1} spacing={0} sx={{ padding: 0 }}>
                    <Grid item xs={12} sx={{ height: { xs: 300, sm: 500 } }}>
                        {trialCountersLoading ? <Skeleton /> : Chart(dataMain)}
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
};

export default SextforceMetricsTrialCountersGraph;
